<template>
  <div
    :class="[
      `flex items-center justify-between border rounded-[${rounded}] p-1 border-[#706F6F]`,
      containerClass,
      { 'w-full': fullWidth, 'w-auto': !fullWidth },
    ]"
  >
    <button
      :disabled="quantity <= min"
      class="text-[#706F6F] pl-2 disabled:opacity-50 disabled:cursor-not-allowed"
      @click.stop.prevent="decrement"
    >
      -
    </button>

    <div class="flex flex-row items-center gap-1 min-w-10">
      <UInput
        v-model="quantity"
        variant="none"
        :input-class="`text-gray-700 font-medium p-0 text-right w-10 pr-4`"
        :ui="{
          root: 'w-auto',
          icon: {
            trailing: {
              padding: {
                sm: 'px-0',
              },
            },
          },
          base: 'text-center text-sm',
        }"
        @update:model-value="emitUpdate"
        @click.stop.prevent
        @blur="blurQuantity"
      />
    </div>

    <button
      :disabled="max > 0 && quantity >= max"
      class="text-[#706F6F] pr-2 disabled:opacity-50 disabled:cursor-not-allowed"
      @click.stop.prevent="increment"
    >
      +
    </button>
  </div>
</template>

<script setup lang="ts">
  interface DjQuantityProps {
    modelValue: number;
    min?: number;
    max?: number;
    step?: number;
    fullWidth?: boolean;
    containerClass?: string;
    rounded?: string;
  }
  const props = withDefaults(defineProps<DjQuantityProps>(), {
    modelValue: 1,
    min: 0,
    max: 0,
    step: 1,
    fullWidth: false,
    containerClass: '',
    rounded: '4px',
  });

  const quantity = ref(props.modelValue);

  const increment = () => {
    if (props.max === 0 || quantity.value < props.max) {
      quantity.value += props.step;
      emitUpdate();
    }
  };

  const decrement = () => {
    if (quantity.value > props.min) {
      quantity.value -= props.step;
      emitUpdate();
    }
  };

  const blurQuantity = () => {
    if (!quantity.value) quantity.value = 1;
  };

  const emit = defineEmits<{
    'update:modelValue': [quantity: number];
  }>();

  function emitUpdate() {
    if (props.max > 0 && quantity.value > props.max) {
      quantity.value = props.max;
    }
    if (quantity.value < props.min) {
      quantity.value = props.min;
    }
    emit('update:modelValue', quantity.value);
  }

  // The component can be reused for another line (paginated tables), so follow the parent value
  watch(
    () => props.modelValue,
    (newValue: number) => {
      if (newValue !== quantity.value) quantity.value = newValue;
    }
  );

  watch(quantity, (newValue: number) => {
    if (props.max > 0 && newValue > props.max) {
      quantity.value = props.max;
      emitUpdate();
    }
    if (newValue < props.min) {
      quantity.value = props.min;
      emitUpdate();
    }
  });
</script>
